import React from 'react';

import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Button from '@material-ui/core/Button';
import withWidth, { isWidthDown, WithWidth } from '@material-ui/core/withWidth';

import { updateCalendarEventFilters } from '../../action/display-settings/update-calendar-event-filters';
import { updateHideCalendarDisabledEvents } from '../../action/display-settings/update-hide-calendar-disabled-events';
import { userSelector } from '../../reducers/current-user-selectors';
import {
  calendarEventFilters,
  hideCalendarDisabledEvents,
} from '../../reducers/display-settings-selectors';

import { UiButton } from '../../ui/ui-button';
import { UiFilterToggle } from '../../ui/ui-filter-toggle';
import { UiMultiselectFilter } from '../../ui/ui-multiselect-filter';

import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { HIRING_PERMISSION, EMPLOYEE_PERMISSION } from '../../util/permissions';
import { CalendarDisplayType } from '../utils/calendar-display-type';

type InputProps = {
  calendarStart: Moment;
  setCalendarStart: (newDate: Moment) => void;
  calendarDisplay: ValueOf<typeof CalendarDisplayType>;
};

interface StateProps {
  user: AuthenticatedUser;
  username: string;
  eventFilters: Array<SelectOption<string>>;
  hideDisabledEvents: boolean;
}
const mapStateToProps = (state: any): StateProps => {
  const user = userSelector(state);
  return {
    user,
    username: user.username || '',
    hideDisabledEvents: hideCalendarDisabledEvents(state, user.username),
    eventFilters: calendarEventFilters(state, user.username),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    updateEventFilters: (eventFilters: Array<SelectOption<string>>, username: string) =>
      dispatch(updateCalendarEventFilters(eventFilters, username)),
    updateHideDisabledEvents: (hideDisabledEvents: boolean, username: string) =>
      dispatch(updateHideCalendarDisabledEvents(hideDisabledEvents, username)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps & WithWidth;

export const EventCalendarFilter = withWidth()(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )((props: Props) => {
    const {
      calendarStart,
      setCalendarStart,
      calendarDisplay,
      eventFilters,
      hideDisabledEvents,
      user,
      username,
      actions,
      width,
    } = props;

    const hasHiringPermission = user.hiringPermission !== HIRING_PERMISSION.NONE;
    const hasEmployeePermission = user.employeesPermission !== EMPLOYEE_PERMISSION.NONE;

    const allowedEventFilters = eventFilters.filter((eventFilter) => {
      if (['START_DATE', 'BIRTHDATE'].includes(eventFilter.value)) {
        return hasEmployeePermission;
      }
      return hasHiringPermission;
    });

    const calendarStartChangeInterval =
      calendarDisplay === CalendarDisplayType.MONTH ? 'month' : 'day';

    const handleChangeEventTypeFilter = (updatedShowEventTypes: Array<SelectOption<string>>) =>
      actions.updateEventFilters(updatedShowEventTypes, username);
    const handleChangeDisabledFilter = () =>
      actions.updateHideDisabledEvents(!hideDisabledEvents, username);
    const handleChangeCalendarStart = (changeByAmount: number) => () =>
      setCalendarStart(moment(calendarStart).add(changeByAmount, calendarStartChangeInterval));
    const handleReturnToToday = () => setCalendarStart(moment());

    const onMobile = isWidthDown('sm', width);

    return (
      <div
        className={`card-body u-border-bottom u-display-flex ${
          onMobile ? 'u-flexWrap-wrap u-justifyContent-center' : 'u-justifyContent-spaceBetween'
        }`}
      >
        <div
          className={`u-display-flex u-alignSelf-flexStart ${onMobile ? 'u-padding-bottom' : ''}`}
        >
          <UiMultiselectFilter
            label="Event Type"
            className="u-width-200px"
            filterValueAndOptions={allowedEventFilters}
            onFilterValueChange={handleChangeEventTypeFilter}
          />
          <div className="u-margin-left u-margin-top-auto u-height-30px">
            <UiFilterToggle
              label="Only Active"
              value={hideDisabledEvents}
              selected={hideDisabledEvents}
              onChange={handleChangeDisabledFilter}
            />
          </div>
        </div>
        <div className="u-display-flex u-alignSelf-flexEnd">
          <Button
            onClick={handleChangeCalendarStart(-1)}
            className="u-margin-top-auto u-height-30px"
          >
            <MaterialDesignIcon iconName={ICON_NAMES.KEYBOARD_ARROW_LEFT} />
          </Button>
          <div className="u-margin-top-auto u-height-30px">
            <UiButton size="xs" variant="contained" onClick={handleReturnToToday}>
              {calendarDisplay === CalendarDisplayType.MONTH ? 'Current Month' : 'Today'}
            </UiButton>
          </div>
          <Button
            onClick={handleChangeCalendarStart(1)}
            className="u-margin-top-auto u-height-30px"
          >
            <MaterialDesignIcon iconName={ICON_NAMES.KEYBOARD_ARROW_RIGHT} />
          </Button>
        </div>
      </div>
    );
  }),
);
